
import { defineComponent, ref, reactive, onMounted, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import StarTabBar from '@/components/star/StarTabBar.vue'
import ResultPanel from '@/views/star/result.vue'
import starApi from '@/api/starApi'
import { useStarTheme, useStarRouter } from '@/utils/useStarTheme'
import StrategyCard from '@/components/star/StrategyCard.vue'
import StarTipDialog from '@/components/star/StarTipDialog.vue'
import type { StrategyItem } from '@/components/star/StrategyCard.vue'
import { renderQrCode } from '@/utils/qrcode'
import { useStarTip } from '@/utils/useStarTip'
const filterTabs = [
  { label: '全部', value: 'all' },
  { label: '已订阅', value: 'sub' },
  { label: '跟投中', value: 'follow' }
]

// 生成模拟图表数据（保留供其他地方使用）
function generateMockChart(events: { date: string; eventType: string }[]) {
  const points: { date: string; strategy: number; benchmark: number; eventType: any }[] = []
  let strategy = 0
  let benchmark = 0
  const eventMap = Object.fromEntries(events.map(e => [e.date, e.eventType]))
  for (let i = 0; i < 120; i++) {
    const d = new Date(2024, 7, 1)
    d.setDate(d.getDate() + i * 2)
    const date = d.toISOString().slice(0, 10).replace(/-/g, '')
    strategy += (Math.random() - 0.45) * 2
    benchmark += (Math.random() - 0.48) * 1
    points.push({
      date,
      strategy: parseFloat(strategy.toFixed(2)),
      benchmark: parseFloat(benchmark.toFixed(2)),
      eventType: eventMap[date] as any
    })
  }
  return points
}

const __default__ = defineComponent({
  name: 'StarIndex',
  components: { StarTabBar, StrategyCard, StarTipDialog, ResultPanel },
  setup() {
    const route = useRoute()
    const store = useStore()
    const { isLight, colors } = useStarTheme()
    const { starPush } = useStarRouter()

    // 将当前路由参数透传到对话框页面
    const openDialog = (path: string, title: string, width = 800, height = 600) => {
      const origin = document.location.origin
      const query = new URLSearchParams(route.query as Record<string, string>).toString()
      const url = `${origin}${document.location.pathname}#${path}${query ? '?' + query : ''}`
      window.open(`gw://webkitdlg?width=${width};height=${height};mode=0;title=${title};allowdestroy=1;url=${url}`)
    }

    const activeTopTab = ref('home')
    const activeFilter = ref('all')
    const activePanel = ref<'result' | 'message' | null>(null)
    const openPanel = (panel: 'result' | 'message') => { activePanel.value = panel }
    const closePanel = () => { activePanel.value = null }
    const loading = ref(false)
    const list = ref<StrategyItem[]>([])
    const canCreate = ref(true)
    const blockReason = ref<'noTotal' | 'noRemain' | null>(null)
    const showLoginMask = ref(false)
    const showServiceDialog = ref(false)
    const showServiceQr = ref(false)
    const serviceQrRef = ref<HTMLElement | null>(null)
    const showTokenDialog = ref(false)
    const tokenInput = ref('')

    const { tipDialog, showQrDialog, showTip, showUnlockQr } = useStarTip()

    const showBlockTip = () => {
      const content = blockReason.value === 'noTotal'
        ? '成为星级用户，解锁功能权限'
        : '免费试用次数已用完，升级星级服务，解锁更多使用次数'
      showTip({ title: '温馨提示', content, confirmText: '立即解锁', showCancel: true, onConfirm: showUnlockQr })
    }

    const summary = reactive({
      totalCount: null as number | null,
      avgWinRate: null as string | null,
      avgReturn: null as string | null
    })

    const actions = [
      {
        key: 'stock',
        name: '个股量化',
        desc: '买卖一目了然',
        iconBg: '#2979ff',
        icon: require('@/assets/star/icon-stock.png'),
        handler: () => {
          if (!canCreate.value) { showBlockTip(); return }
          starPush('/star/stock', { isfrom: '1' })
        }
      },
      {
        key: 'group',
        name: '组合量化',
        desc: '策略验证优化',
        iconBg: '#ff6d00',
        icon: require('@/assets/star/icon-group.png'),
        handler: () => {
          if (!canCreate.value) { showBlockTip(); return }
          starPush('/star/group')
        }
      },
      {
        key: 'token',
        name: '策略口令粘贴',
        desc: '导入高手策略',
        iconBg: '#00bcd4',
        icon: require('@/assets/star/icon-token.png'),
        handler: () => {
          if (!canCreate.value) { showBlockTip(); return }
          showTokenDialog.value = true
        }
      },
      {
        key: 'result',
        name: '回测记录',
        desc: '历史回测记录',
        iconBg: '#ff6d00',
        icon: require('@/assets/star/icon-result.png'),
        handler: () => openPanel('result')
      }
    ]

    const fetchList = async (from: string, checkStatus = false) => {
      loading.value = true
      try {
        const token = route.query.token as string
        const userName = store.state.userInfo?.userName
        const res = await starApi.getStrategyList({
          data: { from },
          headers: { token, userName }
        })
        const raw = res?.data?.list || []
        const data = res?.data || {}

        // 同步更新 summary
        summary.totalCount = data.listTotal ?? null
        summary.avgWinRate = data.winRate != null ? (data.winRate * 100).toFixed(2) + '%' : null
        summary.avgReturn = data.totalReturn != null ? (data.totalReturn * 100).toFixed(2) + '%' : null

        // 仅首次加载时处理 status / 次数限制
        if (checkStatus) {
          const status = data.status
          const testNumTotal = data.testNumTotal
          const testNumRemain = data.testNumRemain

          if (status === 2) {
            // 已解锁更多使用权
            showTip({
              title: '温馨提示',
              content: data.toast || '您已解锁更多使用权',
              confirmText: '立即体验',
              showCancel: false,
              onConfirm: () => { tipDialog.visible = false }
            })
          } else if (status === 1) {
            if (testNumTotal === 0) {
              // 免费用户，7天试用到期
              canCreate.value = false
              blockReason.value = 'noTotal'
              showTip({
                title: '温馨提示',
                content: '成为星级用户，解锁功能权限',
                confirmText: '立即解锁',
                showCancel: true,
                onConfirm: showUnlockQr
              })
            } else if (testNumTotal !== 99999) {
              // 星装用户 + 一星用户，判断今日剩余次数
              if (testNumRemain === 0) {
                // 今日试用次数用完
                canCreate.value = false
                blockReason.value = 'noRemain'
                showTip({
                  title: '温馨提示',
                  content: '免费试用次数已用完，升级星级服务，解锁更多使用次数',
                  confirmText: '立即解锁',
                  showCancel: true,
                  onConfirm: showUnlockQr
                })
              }
              // testNumRemain > 0：今日次数未用完，可继续使用，无障碍
            }
            // testNumTotal === 99999：不限次用户，无障碍
          }
        }
        const actionMap: Record<number, string> = { 1: 'subscribe', 2: 'follow', 3: 'interrupted' }
        list.value = raw.map((item: any) => {
          const type = item.quantType === 3 ? 'group' : 'stock'
          let followStatus: 'following' | 'interrupted' | null = null
          if (item.isFollowing) followStatus = 'following'
          let subscribeStatus: 'following' | 'interrupted' | 'subscribed' | null = null
          if (item.isFollowing) subscribeStatus = 'following'
          else if (item.isSubscribed) subscribeStatus = 'subscribed'
          const chartData = (item.returnList || []).map((p: any) => ({
            date: String(p.d),
            strategy: (p.v ?? 0) * 100,
            benchmark: (p.b ?? 0) * 100,
            eventType: p.a ? actionMap[p.a] : undefined
          }))
          return {
            id: item.taskID,
            name: item.title,
            type,
            quantType: item.quantType,
            followStatus,
            subscribeStatus,
            strategyReturn: item.totalReturn != null ? item.totalReturn * 100 : 0,
            maxDrawdown: item.riskRatio != null ? item.riskRatio * 100 : 0,
            profitLossRatio: item.riskRatio ?? 0,
            winRate: item.winRate,
            rightTag: item.rightTag,
            leftTags: item.leftTags,
            chartData
          }
        })
      } catch (e) {
        list.value = []
      } finally {
        loading.value = false
      }
    }

    // 点击立即开通后生成服务协议二维码
    watch(showServiceQr, async (val) => {
      if (val) {
        await nextTick()
        if (serviceQrRef.value) renderQrCode(serviceQrRef.value, 'https://test-s.jinyi999.cn/vh326n', 120)
      }
    })

    // 权限初始化入口
    const initAuth = async () => {
      const token = route.query.token as string

      if (!token) {
        showLoginMask.value = true
        console.log('gw://OnUserLogin')
        window.open('gw://OnUserLogin')
        return
      }

      // 从路由参数直接取 userName 和 userType，不再调 tokenLogin
      const userName = route.query.username as string
      const userType = route.query.userType as string

      if (!userName) {
        showLoginMask.value = true
        console.log('gw://OnUserLogin')
        window.open('gw://OnUserLogin')
        return
      }

      store.commit('setUserInfo', { userName, userType })

      try {
        // 请求 homeCheck
        const checkRes = await starApi.homeCheck({
          headers: { userName, token }
        })
        const homeInService = checkRes?.data?.homeInService

        if (homeInService) {
          showServiceDialog.value = false
          fetchList(activeFilter.value, true)
        } else {
          showServiceDialog.value = true
        }
      } catch (e) {
        console.error('权限初始化失败', e)
      }
    }

    const switchFilter = (value: string) => {
      if (activeFilter.value === value) return
      activeFilter.value = value
      fetchList(value)
    }

    const confirmToken = async () => {
      const word = tokenInput.value.trim()
      if (!word) return

      const token = route.query.token as string
      const userName = store.state.userInfo?.userName

      try {
        const res = await starApi.redeemWatchword({
          data: { watchWord: word },
          headers: { token, userName }
        })

        // status: 1 正常，2 兑换故障；优先判断 status
        if (res?.data?.status === 2) {
          showTip({ title: '提示', content: res?.data?.toast || '口令无效，请重新输入' })
          return
        }

        // 兜底：接口本身报错（无 data 或其他异常 code）
        if (!res?.data) {
          showTip({ title: '提示', content: res?.message || '口令无效，请检查后重试' })
          return
        }

        showTokenDialog.value = false
        tokenInput.value = ''
        starPush('/star/group', { isPassword: 'true', watchWord: word })
      } catch (e) {
        showTip({ title: '提示', content: '口令兑换失败，请稍后重试' })
      }
    }

    const handleFollow = (item: StrategyItem) => {
      console.log('一键跟投', item)
    }

    const handleLive = (item: StrategyItem) => {
      console.log('去实盘查看', item)
    }

    const handleCreate = () => {
      if (!canCreate.value) {
        showBlockTip()
        return
      }
      starPush('/star/stock')
    }

    onMounted(() => {
      initAuth()
    })

    return {
      isLight, colors,
      activeTopTab, activeFilter, filterTabs,
      activePanel, openPanel, closePanel,
      loading, list, summary, actions, canCreate, showLoginMask, showServiceDialog, showServiceQr, serviceQrRef,
      showTokenDialog, tokenInput,
      showQrDialog, tipDialog,
      switchFilter, confirmToken, handleFollow, handleLive, handleCreate, openDialog, initAuth
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "52fd2e23": (_ctx.isLight ? '#666666' : 'rgba(255,255,255,0.45)'),
  "48ac1d32": (_ctx.isLight ? '#333333' : 'rgba(255,255,255,0.8)'),
  "dcc8c884": (_ctx.isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.35)'),
  "c7bf93e4": (_ctx.isLight ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.55)'),
  "9d731064": (_ctx.isLight ? 'transparent' : 'linear-gradient(180deg, #252525 0%, #262626 100%)'),
  "3f69d170": (_ctx.isLight ? '1px solid #dbdfe6' : 'none'),
  "44c11bde": (_ctx.isLight ? '0 2px 8px rgba(0,0,0,0.06)' : 'none')
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__